import React, { forwardRef, useEffect, useRef, useState, useCallback } from 'react';
import ImageEditPanel from './ImageEditPanel';

const EditorCanvas = forwardRef(({ 
  badge, 
  badges, 
  orientation, 
  zoom, 
  panOffset, 
  selectedBadgeIndex,
  onSelectBadge,
  onUpdateBadges 
}, ref) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [loadedImages, setLoadedImages] = useState({});
  const [panelHidden, setPanelHidden] = useState(false);
  const [isDraggingImage, setIsDraggingImage] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialOffset, setInitialOffset] = useState({ x: 0, y: 0 });

  // A4 dimensions in mm
  const A4_WIDTH_MM = 210;
  const A4_HEIGHT_MM = 297;
  const MM_TO_PX = 3.7795; // Conversion factor

  // Pre-load images - track all unique image URLs
  useEffect(() => {
    badges.forEach((badgeData) => {
      badgeData.layers.forEach((layer) => {
        if (layer.type === 'image' && layer.url) {
          const url = layer.url;
          // Use functional update to avoid stale closure
          setLoadedImages(prev => {
            if (prev[url]) return prev; // Already loaded or loading
            
            // Create placeholder to prevent double-loading
            const newState = { ...prev, [url]: null };
            
            // Start loading the image
            const img = new Image();
            img.onload = () => {
              setLoadedImages(p => ({ ...p, [url]: img }));
            };
            img.onerror = () => {
              console.error('Failed to load image:', url.substring(0, 50) + '...');
              // Remove placeholder on error
              setLoadedImages(p => {
                const { [url]: _, ...rest } = p;
                return rest;
              });
            };
            img.src = url;
            
            return newState;
          });
        }
      });
    });
  }, [badges]);

  // Get selected badge's image layer
  const getSelectedImageLayer = useCallback(() => {
    if (selectedBadgeIndex < 0 || selectedBadgeIndex >= badges.length) return null;
    const selectedBadge = badges[selectedBadgeIndex];
    return selectedBadge?.layers?.find(layer => layer.type === 'image');
  }, [badges, selectedBadgeIndex]);

  // Calculate panel position - memoized to avoid setState in effect
  const calculatedPanelPosition = React.useMemo(() => {
    const imageLayer = getSelectedImageLayer();
    if (imageLayer && selectedBadgeIndex >= 0 && selectedBadgeIndex < badges.length) {
      const selectedBadge = badges[selectedBadgeIndex];
      if (selectedBadge) {
        const badgeCenterX = selectedBadge.x * MM_TO_PX * zoom + (selectedBadge.width * MM_TO_PX * zoom) / 2;
        const badgeCenterY = selectedBadge.y * MM_TO_PX * zoom + (selectedBadge.height * MM_TO_PX * zoom);
        return {
          x: badgeCenterX,
          y: badgeCenterY + 20,
          show: true
        };
      }
    }
    return { x: 0, y: 0, show: false };
  }, [selectedBadgeIndex, badges, zoom, getSelectedImageLayer]);

  // Draw canvas
  useEffect(() => {
    if (!canvasRef.current || !badges.length) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Set canvas size
    const canvasWidth = A4_WIDTH_MM * MM_TO_PX * zoom;
    const canvasHeight = A4_HEIGHT_MM * MM_TO_PX * zoom;
    
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Clear canvas
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Draw A4 paper outline
    ctx.strokeStyle = '#e5e5e5';
    ctx.lineWidth = 2;
    ctx.strokeRect(0, 0, canvasWidth, canvasHeight);

    // Draw badges
    badges.forEach((badgeData, index) => {
      const isSelected = index === selectedBadgeIndex;
      const x = badgeData.x * MM_TO_PX * zoom;
      const y = badgeData.y * MM_TO_PX * zoom;
      const designWidth = badgeData.width * MM_TO_PX * zoom;  // 70mm
      const designHeight = badgeData.height * MM_TO_PX * zoom;
      
      // Badge size where image fills (58mm for 58mm badge)
      const badgeWidth = (badge.badgeWidth || badge.badgeSize) * MM_TO_PX * zoom;
      const badgeHeight = (badge.badgeHeight || badge.badgeSize) * MM_TO_PX * zoom;
      
      // Center of design area
      const centerX = x + designWidth / 2;
      const centerY = y + designHeight / 2;

      // Draw badge background (full design area 70mm)
      ctx.save();
      
      if (badge.type === 'round') {
        ctx.beginPath();
        ctx.arc(centerX, centerY, designWidth / 2, 0, Math.PI * 2);
        ctx.clip();
      } else {
        ctx.beginPath();
        ctx.rect(x, y, designWidth, designHeight);
        ctx.clip();
      }

      // White background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(x, y, designWidth, designHeight);

      // Draw image - fills to badge size (58mm for 58mm badge)
      badgeData.layers.slice(1).forEach(layer => {
        if (layer.type === 'image' && layer.url) {
          const img = loadedImages[layer.url];
          if (img && img.complete && img.naturalWidth > 0) {
            const userScale = layer.scale || 1;
            const imgWidth = img.naturalWidth;
            const imgHeight = img.naturalHeight;
            const imgAspect = imgWidth / imgHeight;
            
            // For circular badges, we need to ensure the image COVERS the entire circle
            // The image must be scaled so that:
            // 1. For landscape images (width > height): the HEIGHT fills the diameter
            // 2. For portrait images (height > width): the WIDTH fills the diameter
            // This ensures the smaller dimension always matches or exceeds the circle diameter
            
            let drawWidth, drawHeight;
            
            if (badge.type === 'round') {
              // For round badges, image must COVER the entire circle
              const diameter = badgeWidth;
              
              if (imgAspect >= 1) {
                // Landscape image (wider than tall) - height fills diameter
                drawHeight = diameter * userScale;
                drawWidth = drawHeight * imgAspect;
              } else {
                // Portrait image (taller than wide) - width fills diameter
                drawWidth = diameter * userScale;
                drawHeight = drawWidth / imgAspect;
              }
            } else {
              // For square/rectangle badges, cover the badge area
              const targetAspect = badgeWidth / badgeHeight;
              
              if (imgAspect > targetAspect) {
                // Image is wider - fit to height
                drawHeight = badgeHeight * userScale;
                drawWidth = drawHeight * imgAspect;
              } else {
                // Image is taller - fit to width
                drawWidth = badgeWidth * userScale;
                drawHeight = drawWidth / imgAspect;
              }
            }
            
            // Add explicit margin to image size to hide anti-aliasing edge artifacts
            // This is the recommended approach from canvas best practices
            const edgeMargin = 3; // pixels - enough to cover any AA artifacts
            drawWidth += edgeMargin * 2;
            drawHeight += edgeMargin * 2;
            
            // Apply position offset (from pan/drag)
            const offsetX = (layer.offsetX || 0) * (drawWidth - edgeMargin * 2) / 100;
            const offsetY = (layer.offsetY || 0) * (drawHeight - edgeMargin * 2) / 100;
            
            // Center image with offset
            const drawX = centerX - drawWidth / 2 + offsetX;
            const drawY = centerY - drawHeight / 2 + offsetY;
            
            // Clip to EXACT badge size (58mm radius)
            // The clipping path defines where the image appears
            ctx.save();
            ctx.beginPath();
            if (badge.type === 'round') {
              ctx.arc(centerX, centerY, badgeWidth / 2, 0, Math.PI * 2);
            } else {
              ctx.rect(centerX - badgeWidth/2, centerY - badgeHeight/2, badgeWidth, badgeHeight);
            }
            ctx.clip();
            
            // Draw the OVERSIZED image - it extends beyond the clip, 
            // ensuring complete fill with no edge gaps
            ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
            ctx.restore();
          }
        }
      });

      ctx.restore();

      // === DRAW GUIDES ===
      
      // 1. BADGE SIZE line (58mm) - where image fills
      ctx.strokeStyle = '#888888';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([]);
      
      if (badge.type === 'round') {
        ctx.beginPath();
        ctx.arc(centerX, centerY, badgeWidth / 2, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        ctx.strokeRect(centerX - badgeWidth/2, centerY - badgeHeight/2, badgeWidth, badgeHeight);
      }

      // 2. CUTTING LINE (70mm) - black dashed - where to cut paper
      ctx.strokeStyle = isSelected ? '#E83C8C' : '#000000';
      ctx.lineWidth = isSelected ? 2.5 : 2;
      ctx.setLineDash([8, 4]);

      if (badge.type === 'round') {
        ctx.beginPath();
        ctx.arc(centerX, centerY, designWidth / 2, 0, Math.PI * 2);
        ctx.stroke();
      } else {
        ctx.strokeRect(x, y, designWidth, designHeight);
      }

      ctx.setLineDash([]);

      // Selection highlight
      if (isSelected) {
        ctx.strokeStyle = '#E83C8C';
        ctx.lineWidth = 3;
        if (badge.type === 'round') {
          ctx.beginPath();
          ctx.arc(centerX, centerY, designWidth / 2 + 5, 0, Math.PI * 2);
          ctx.stroke();
        } else {
          ctx.strokeRect(x - 3, y - 3, designWidth + 6, designHeight + 6);
        }
      }
    });
  }, [badge, badges, zoom, selectedBadgeIndex, loadedImages]);

  // Handle mouse down for image dragging
  const handleMouseDown = (e) => {
    const imageLayer = getSelectedImageLayer();
    if (!imageLayer || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoom;
    const y = (e.clientY - rect.top) / zoom;

    // Check if clicking within selected badge
    const selectedBadge = badges[selectedBadgeIndex];
    if (selectedBadge) {
      const bx = selectedBadge.x * MM_TO_PX;
      const by = selectedBadge.y * MM_TO_PX;
      const bw = selectedBadge.width * MM_TO_PX;
      const bh = selectedBadge.height * MM_TO_PX;

      if (x >= bx && x <= bx + bw && y >= by && y <= by + bh) {
        setIsDraggingImage(true);
        setDragStart({ x: e.clientX, y: e.clientY });
        setInitialOffset({ x: imageLayer.offsetX || 0, y: imageLayer.offsetY || 0 });
        e.preventDefault();
      }
    }
  };

  // Handle mouse move for image dragging
  const handleMouseMove = useCallback((e) => {
    if (!isDraggingImage) return;

    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;

    // Convert pixel delta to percentage offset (sensitivity adjustment)
    const sensitivity = 0.5;
    const newOffsetX = Math.max(-100, Math.min(100, initialOffset.x + deltaX * sensitivity));
    const newOffsetY = Math.max(-100, Math.min(100, initialOffset.y + deltaY * sensitivity));

    // Update the image layer
    const updatedBadges = badges.map((b, i) => {
      if (i === selectedBadgeIndex) {
        return {
          ...b,
          layers: b.layers.map(layer => 
            layer.type === 'image' 
              ? { ...layer, offsetX: Math.round(newOffsetX), offsetY: Math.round(newOffsetY) }
              : layer
          )
        };
      }
      return b;
    });
    onUpdateBadges(updatedBadges);
  }, [isDraggingImage, dragStart, initialOffset, badges, selectedBadgeIndex, onUpdateBadges]);

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    setIsDraggingImage(false);
  }, []);

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDraggingImage) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDraggingImage, handleMouseMove, handleMouseUp]);

  const handleWheel = (e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      // Zoom handled by parent
    }
  };

  // Image panel callbacks
  const handleUpdateImage = (updatedLayer) => {
    const updatedBadges = badges.map((b, i) => {
      if (i === selectedBadgeIndex) {
        return {
          ...b,
          layers: b.layers.map(layer => 
            layer.type === 'image' ? { ...layer, ...updatedLayer } : layer
          )
        };
      }
      return b;
    });
    onUpdateBadges(updatedBadges);
  };

  const handleRemoveImage = () => {
    const updatedBadges = badges.map((b, i) => {
      if (i === selectedBadgeIndex) {
        return {
          ...b,
          layers: b.layers.filter(layer => layer.type !== 'image')
        };
      }
      return b;
    });
    onUpdateBadges(updatedBadges);
    setPanelHidden(true);
  };

  const handleApplyToAll = () => {
    const imageLayer = getSelectedImageLayer();
    if (!imageLayer) return;

    const updatedBadges = badges.map(b => {
      // Check if badge already has an image
      const hasImage = b.layers.some(layer => layer.type === 'image');
      if (hasImage) {
        return {
          ...b,
          layers: b.layers.map(layer => 
            layer.type === 'image' 
              ? { ...layer, ...imageLayer } 
              : layer
          )
        };
      } else {
        // Add the image layer to badges that don't have one
        return {
          ...b,
          layers: [...b.layers, { ...imageLayer, id: `img-${Date.now()}-${Math.random()}` }]
        };
      }
    });
    onUpdateBadges(updatedBadges);
  };

  const imageLayer = getSelectedImageLayer();
  
  // Show panel if there's an image and the memoized position says to show it
  // panelHidden is only used to close the panel manually (via X button or remove)
  const showPanel = calculatedPanelPosition.show && imageLayer && !panelHidden;

  // When user closes panel manually, clicking a new badge should reopen it
  const handleBadgeClick = (index) => {
    if (index !== selectedBadgeIndex) {
      setPanelHidden(false);
    }
    onSelectBadge(index);
  };

  return (
    <div 
      ref={containerRef}
      className="relative inline-block"
      onWheel={handleWheel}
    >
      <canvas
        ref={canvasRef}
        onClick={(e) => {
          if (isDraggingImage) return;
          if (!canvasRef.current) return;

          const rect = canvasRef.current.getBoundingClientRect();
          const x = (e.clientX - rect.left) / zoom;
          const y = (e.clientY - rect.top) / zoom;

          // Find clicked badge
          let clickedBadgeIndex = -1;
          badges.forEach((badgeData, index) => {
            const bx = badgeData.x * MM_TO_PX;
            const by = badgeData.y * MM_TO_PX;
            const bw = badgeData.width * MM_TO_PX;
            const bh = badgeData.height * MM_TO_PX;

            if (x >= bx && x <= bx + bw && y >= by && y <= by + bh) {
              clickedBadgeIndex = index;
            }
          });

          if (clickedBadgeIndex >= 0) {
            handleBadgeClick(clickedBadgeIndex);
          }
        }}
        onMouseDown={handleMouseDown}
        className={`shadow-2xl ${isDraggingImage ? 'cursor-grabbing' : 'cursor-crosshair'}`}
        style={{
          display: 'block'
        }}
      />

      {/* Floating Image Edit Panel */}
      {showPanel && (
        <ImageEditPanel
          position={calculatedPanelPosition}
          imageLayer={imageLayer}
          onUpdateImage={handleUpdateImage}
          onRemoveImage={handleRemoveImage}
          onApplyToAll={handleApplyToAll}
          onClose={() => setPanelHidden(true)}
        />
      )}
    </div>
  );
});

EditorCanvas.displayName = 'EditorCanvas';

export default EditorCanvas;
